/**
 * BigStatRow
 *
 * IMD Annual Report 스타일의 빅 타이포그래피 통계 카드
 * - 상단: 아이콘 + 라벨 텍스트
 * - 하단: 초대형 숫자 (Framer Motion 애니메이션)
 *
 * @param {number} value - 표시할 숫자 [Required]
 * @param {string} label - 상단 라벨 텍스트 [Required]
 * @param {string} [type] - 아이콘 타입 ('threads'|'view'|'like'|'reply'|'repost'|'month')
 * @param {string} [prefix] - 숫자 앞 접두어 (예: '+')
 * @param {boolean} [enableAnimation=true] - CountUp 애니메이션 사용
 * @param {boolean} [isMobile] - 모바일 여부
 * @param {number} [duration=2.5] - 애니메이션 지속 시간 (초)
 * @param {number} [delay=0] - 애니메이션 시작 딜레이 (ms)
 * @param {boolean} [isLoading=false] - 로딩 상태
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useInView, animate } from 'framer-motion';
import { IconView } from '../../assets/icon/metric/IconView';
import { IconLike } from '../../assets/icon/metric/IconLike';
import { IconReply } from '../../assets/icon/metric/IconReply';
import { IconRepost } from '../../assets/icon/metric/IconRepost';
import { IconThreads } from '../../assets/icon/metric/IconThreads';
import { IconCalendarMonth } from '@tabler/icons-react';

// 타입별 아이콘 매핑
const IconMap = {
  threads: IconThreads,
  view: IconView,
  like: IconLike,
  reply: IconReply,
  repost: IconRepost,
  month: IconCalendarMonth,
};

// Framer Motion 기반 카운터 훅
const useAnimatedCounter = (end, duration = 2.5, delay = 0, enabled = true) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!enabled || !isInView || hasAnimated.current) return;

    hasAnimated.current = true;
    const timeout = setTimeout(() => {
      const controls = animate(0, end, {
        duration,
        ease: [0.25, 0.1, 0.25, 1], // cubic-bezier easing
        onUpdate: (latest) => setCount(Math.round(latest)),
      });

      return () => controls.stop();
    }, delay);

    return () => clearTimeout(timeout);
  }, [isInView, end, duration, delay, enabled]);

  return { count, ref };
};

// 숫자 포맷팅 (천 단위 구분자)
const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  return num.toLocaleString();
};

export const BigStatRow = ({
  value,
  label,
  type,
  prefix = '',
  enableAnimation = true,
  isMobile = false,
  duration = 2.5,
  delay = 0,
  isLoading = false,
}) => {
  const theme = useTheme();
  const { count, ref } = useAnimatedCounter(
    value || 0,
    duration,
    delay,
    enableAnimation && !isLoading
  );

  // 아이콘 컴포넌트 가져오기
  const IconComponent = type ? IconMap[type] : null;

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', py: { xs: 2, md: 3 } }}>
        <Skeleton
          variant="text"
          sx={{
            width: { xs: 70, sm: 80, md: 100 },
            height: 20,
            mb: 1
          }}
        />
        <Skeleton
          variant="text"
          sx={{
            width: { xs: 90, sm: 120, md: 160 },
            height: { xs: 28, sm: 36, md: 60, lg: 72 }
          }}
        />
      </Box>
    );
  }

  return (
    <Box ref={ref} sx={{ width: '100%', py: { xs: 2, md: 3 } }}>
      {/* 상단: 아이콘 + 라벨 */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        {IconComponent && (
          <Box
            sx={{
              padding: theme.spacing(0.25),
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.paper,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              height: 'fit-content',
            }}
          >
            <IconComponent size={isMobile ? 14 : 16} />
          </Box>
        )}
        <Typography
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
          }}
        >
          {label}
        </Typography>
      </Stack>

      {/* 하단: 초대형 숫자 */}
      <Typography
        component="div"
        sx={{
          fontFamily: '"SUIT Variable", sans-serif',
          fontSize: {
            xs: '1.5rem',    // 360px~ (매우 작은 모바일)
            sm: '1.75rem',   // 600px~ (일반 모바일)
            md: '2.25rem',   // 900px~ (태블릿)
            lg: '2.5rem',    // 1200px~ (데스크톱)
          },
          fontWeight: 800,
          lineHeight: 1,
          letterSpacing: {
            xs: '-0.02em',
            sm: '-0.03em',
            md: '-0.04em',
            lg: '-0.05em',
          },
          fontFeatureSettings: '"tnum" on, "lnum" on',
          color: theme.palette.text.primary,
        }}
      >
        {prefix && (
          <Box component="span" sx={{ mr: 0.5 }}>
            {prefix}
          </Box>
        )}
        {enableAnimation ? formatNumber(count) : formatNumber(value)}
      </Typography>
    </Box>
  );
};

export default BigStatRow;
